import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Play, Pause, RotateCcw, MapPin, History, Gauge, ChevronsRight } from "lucide-react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Rastro Histórico — playback (24h) das posições GPS por entregador.
// v6 (fev/2026): TTL de entregador_positions é 24h, então esse é o teto real
// da janela. Slider varre a lista de posições (não o tempo linear) — assim
// gaps grandes de tempo passam rápido e trechos com muitos pings dão mais
// resolução ao operador.

const SPEEDS = [1, 2, 5, 10];
const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { hour12: false });
  } catch {
    return iso;
  }
};

function motoIcon(nome, atEnd) {
  const bg = atEnd ? "#7c3aed" : "#f97316"; // laranja em movimento, violeta ao fim
  // SVG inline de moto (Font Awesome-like) — branco no fundo colorido
  const svg = `<svg viewBox="0 0 640 512" width="22" height="22" fill="#fff" xmlns="http://www.w3.org/2000/svg"><path d="M512 176c-15.9 0-31.1 3.5-44.7 9.7L446.4 128H480c8.8 0 16-7.2 16-16V96c0-8.8-7.2-16-16-16h-64c-17.7 0-32 14.3-32 32v20.4l24.3 65.7C388.4 205.6 373.4 224 353 224H262.6l-38.9-97.4C219.3 115 208.9 108.4 197.5 106.6L96.2 90.7C77.9 87.9 64 103.4 64 121.9V128h32c8.8 0 16 7.2 16 16v32c0 26.5 21.5 48 48 48h20l-17.6 35.3C129.6 260.6 96 300.4 96 348c0 55.2 44.8 100 100 100c47.6 0 87.4-33.6 97-78.4l24.2-48.4c19-38 57.9-61.2 100.2-61.2h4c11.9 44.6 52.5 78 100.6 78c57.4 0 104-46.6 104-104s-46.6-104-104-104zM195 400a52 52 0 1 1 0-104a52 52 0 1 1 0 104zm317-52a52 52 0 1 1 -104 0a52 52 0 1 1 104 0z"/></svg>`;
  return L.divIcon({
    className: "",
    html: `<div style="position:relative">
      <div style="background:${bg};border:3px solid #fff;border-radius:9999px;width:44px;height:44px;box-shadow:0 6px 14px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;">${svg}</div>
      ${nome ? `<div style="position:absolute;top:44px;left:50%;transform:translateX(-50%);background:#0f172a;color:#fff;padding:2px 6px;border-radius:6px;font-size:10px;white-space:nowrap;font-weight:700;">${nome.split(" ")[0]}</div>` : ""}
    </div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
}

// Ícone menor pros pontos "início" e "fim" do trajeto
function dotIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:14px;height:14px;background:${color};border:2px solid #fff;border-radius:9999px;box-shadow:0 2px 6px rgba(0,0,0,.3);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

// Marker de evento (saída/conclusão/remarcada/cancelada) com tooltip do horário.
// v6.2 (fev/2026): agora também suporta remarcada (âmbar ↻) e cancelada (rosa ✕).
function eventIcon(tipo) {
  const map = {
    conclusao: { bg: "#059669", label: "✓" }, // verde
    saida:     { bg: "#0284c7", label: "▶" }, // azul
    remarcada: { bg: "#d97706", label: "↻" }, // âmbar
    cancelada: { bg: "#dc2626", label: "✕" }, // vermelho
  };
  const cfg = map[tipo] || map.saida;
  return L.divIcon({
    className: "",
    html: `<div style="background:${cfg.bg};color:#fff;border:3px solid #fff;border-radius:9999px;width:26px;height:26px;box-shadow:0 4px 10px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;font-weight:800;font-size:13px;">${cfg.label}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

// Encontra o índice de posição com captured_at mais próximo de `iso`.
// Retorna null se não achar nada em janela de 3min.
function findNearestPositionIdx(positions, iso) {
  if (!iso || !positions?.length) return null;
  const target = new Date(iso).getTime();
  if (!Number.isFinite(target)) return null;
  let best = null;
  positions.forEach((p, i) => {
    const t = new Date(p.captured_at || p.server_at || 0).getTime();
    if (!Number.isFinite(t)) return;
    const diff = Math.abs(t - target);
    if (best === null || diff < best.diff) best = { i, diff };
  });
  if (!best) return null;
  if (best.diff > 3 * 60 * 1000) return null; // gap >3min → ponto muito distante
  return best.i;
}

// Retorna true se `iso` cai dentro de alguma janela [start, end]
function isInTransit(iso, windows) {
  if (!iso || !windows?.length) return false;
  return windows.some((w) => w.start && w.end && iso >= w.start && iso <= w.end);
}

function HistoricoMap({ positions, events, transitWindows, cursor, driverName }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const polyLayersRef = useRef([]);
  const cursorMarkerRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const eventMarkersRef = useRef([]);
  const fittedRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true }).setView(
      [-16.3287, -48.9530],
      12
    );
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      polyLayersRef.current = [];
      cursorMarkerRef.current = null;
      startMarkerRef.current = null;
      endMarkerRef.current = null;
      eventMarkersRef.current = [];
      fittedRef.current = false;
    };
  }, []);

  // Redesenha polylines + markers de evento quando muda a lista de posições
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Limpa camadas anteriores
    polyLayersRef.current.forEach((l) => map.removeLayer(l));
    polyLayersRef.current = [];
    if (startMarkerRef.current) { map.removeLayer(startMarkerRef.current); startMarkerRef.current = null; }
    if (endMarkerRef.current) { map.removeLayer(endMarkerRef.current); endMarkerRef.current = null; }
    eventMarkersRef.current.forEach((m) => map.removeLayer(m));
    eventMarkersRef.current = [];
    fittedRef.current = false;
    if (!positions || positions.length === 0) {
      if (cursorMarkerRef.current) { map.removeLayer(cursorMarkerRef.current); cursorMarkerRef.current = null; }
      return;
    }
    // Segmenta polyline por status (em_transito=azul, fora=cinza)
    // Para cada par de pontos consecutivos, avalia se AMBOS estão em uma janela
    // de em_transito. Se sim → azul. Senão → cinza. Segmentos verdes serão
    // adicionados apenas nos markers de conclusão (não como linha inteira, já
    // que a moto para no destino).
    const clean = positions.filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));
    for (let i = 0; i < clean.length - 1; i++) {
      const a = clean[i];
      const b = clean[i + 1];
      const inTransit =
        isInTransit(a.captured_at || a.server_at, transitWindows) &&
        isInTransit(b.captured_at || b.server_at, transitWindows);
      const line = L.polyline(
        [[a.latitude, a.longitude], [b.latitude, b.longitude]],
        {
          color: inTransit ? "#2563eb" : "#94a3b8",
          weight: inTransit ? 4.5 : 3,
          opacity: inTransit ? 0.9 : 0.55,
          dashArray: inTransit ? null : "6,6",
          lineCap: "round",
          lineJoin: "round",
        }
      ).addTo(map);
      polyLayersRef.current.push(line);
    }
    const first = clean[0];
    const last = clean[clean.length - 1];
    startMarkerRef.current = L.marker([first.latitude, first.longitude], { icon: dotIcon("#059669") })
      .addTo(map)
      .bindTooltip(`Início · ${fmtTs(first.captured_at || first.server_at)}`, { direction: "top" });
    endMarkerRef.current = L.marker([last.latitude, last.longitude], { icon: dotIcon("#dc2626") })
      .addTo(map)
      .bindTooltip(`Última posição · ${fmtTs(last.captured_at || last.server_at)}`, { direction: "top" });

    // Markers de evento (saída/conclusão/remarcada/cancelada) — mapeia pra posição mais próxima
    const eventLabels = {
      saida: "▶ Saída da loja",
      conclusao: "✓ Entrega concluída",
      remarcada: "↻ Entrega remarcada",
      cancelada: "✕ Entrega cancelada",
    };
    (events || []).forEach((ev) => {
      const idx = findNearestPositionIdx(clean, ev.at);
      if (idx === null) return;
      const p = clean[idx];
      const marker = L.marker([p.latitude, p.longitude], { icon: eventIcon(ev.tipo) }).addTo(map);
      const tipo = eventLabels[ev.tipo] || `▶ ${ev.tipo}`;
      const cliente = ev.cliente_nome || "Cliente";
      const pedido = ev.numero_pedido ? ` · #${ev.numero_pedido}` : "";
      marker.bindTooltip(
        `<div style="font-weight:700;">${tipo}</div>
         <div>${cliente}${pedido}</div>
         <div style="color:#64748b;font-size:11px;">${fmtTs(ev.at)}</div>`,
        { permanent: false, direction: "top", offset: [0, -8] }
      );
      eventMarkersRef.current.push(marker);
    });

    if (!fittedRef.current && clean.length > 0) {
      fittedRef.current = true;
      map.fitBounds(L.latLngBounds(clean.map((p) => [p.latitude, p.longitude])), { padding: [40, 40] });
    }
  }, [positions, events, transitWindows]);

  // Atualiza marker do cursor (moto)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !positions || positions.length === 0) return;
    const idx = Math.max(0, Math.min(positions.length - 1, cursor));
    const p = positions[idx];
    if (!p || !Number.isFinite(p.latitude) || !Number.isFinite(p.longitude)) return;
    const atEnd = idx === positions.length - 1;
    const icon = motoIcon(driverName, atEnd);
    if (cursorMarkerRef.current) {
      cursorMarkerRef.current.setLatLng([p.latitude, p.longitude]);
      cursorMarkerRef.current.setIcon(icon);
    } else {
      cursorMarkerRef.current = L.marker([p.latitude, p.longitude], { icon, zIndexOffset: 1000 }).addTo(map);
    }
  }, [cursor, positions, driverName]);

  return (
    <div
      ref={containerRef}
      data-testid="historico-map"
      style={{ height: "60vh", minHeight: 420, width: "100%", borderRadius: 12, overflow: "hidden" }}
    />
  );
}

export default function RastroHistorico() {
  const { currentEmpresaId } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [positions, setPositions] = useState([]);
  const [events, setEvents] = useState([]);
  const [transitWindows, setTransitWindows] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(5);
  const [trackingDisabled, setTrackingDisabled] = useState(false);
  const rafRef = useRef(null);

  // Carrega lista de entregadores com posições nas últimas 24h
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingDrivers(true);
      try {
        const r = await api.get("/tracking/history/drivers?hours=24");
        if (cancelled) return;
        setTrackingDisabled(!!r.data?.tracking_disabled);
        setDrivers(r.data?.entregadores || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Erro ao listar entregadores");
      } finally {
        if (!cancelled) setLoadingDrivers(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentEmpresaId]);

  // Carrega posições do entregador selecionado
  useEffect(() => {
    if (!selectedId) { setPositions([]); setEvents([]); setTransitWindows([]); setCursor(0); return; }
    let cancelled = false;
    const load = async () => {
      setLoadingPositions(true);
      setPlaying(false);
      try {
        const r = await api.get(`/tracking/history?entregador_id=${selectedId}&hours=24`);
        if (cancelled) return;
        const pts = r.data?.positions || [];
        setPositions(pts);
        setEvents(r.data?.events || []);
        setTransitWindows(r.data?.transit_windows || []);
        setCursor(pts.length > 0 ? pts.length - 1 : 0); // começa no fim (mais recente)
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Erro ao carregar trajeto");
      } finally {
        if (!cancelled) setLoadingPositions(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedId]);

  // Auto-play: avança o cursor com setInterval — speed = pontos por segundo
  useEffect(() => {
    if (!playing || positions.length < 2) return;
    const stepMs = Math.max(50, Math.round(1000 / speed));
    const t = setInterval(() => {
      setCursor((c) => {
        const next = c + 1;
        if (next >= positions.length) {
          setPlaying(false);
          return positions.length - 1;
        }
        return next;
      });
    }, stepMs);
    return () => clearInterval(t);
  }, [playing, positions.length, speed]);

  const selectedDriver = useMemo(
    () => drivers.find((d) => d.entregador_id === selectedId) || null,
    [drivers, selectedId]
  );
  const cursorPos = positions[cursor] || null;

  const handleRestart = () => {
    setCursor(0);
    setPlaying(true);
  };

  return (
    <div className="space-y-4" data-testid="page-rastro-historico">
      {trackingDisabled && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900" data-testid="hist-tracking-disabled">
          Rastreamento GPS está <b>desativado</b> para esta empresa. Sem histórico disponível.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* Sidebar — lista de entregadores com posições no dia */}
        <div className="space-y-3 max-h-[72vh] overflow-y-auto pr-1">
          <Card>
            <CardContent className="p-3">
              <div className="text-xs uppercase font-bold text-violet-800 mb-1.5 flex items-center gap-1.5">
                <History className="w-4 h-4" />
                Últimas 24h · entregadores com trajeto
              </div>
              {loadingDrivers ? (
                <div className="text-sm text-muted-foreground">Carregando…</div>
              ) : drivers.length === 0 ? (
                <div className="text-sm text-muted-foreground" data-testid="hist-empty">
                  Nenhum entregador registrou posições nas últimas 24h.
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Selecione um entregador abaixo para ver o trajeto.
                </div>
              )}
            </CardContent>
          </Card>

          {drivers.map((d) => {
            const isSelected = selectedId === d.entregador_id;
            return (
              <Card
                key={d.entregador_id}
                onClick={() => setSelectedId(d.entregador_id)}
                className={`cursor-pointer transition ${isSelected ? "ring-2 ring-violet-500 shadow-md" : "hover:shadow-md"}`}
                data-testid={`hist-driver-${d.entregador_id}`}
              >
                <CardContent className="p-3">
                  <div className="font-bold truncate">{d.nome}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {d.position_count} ponto{d.position_count !== 1 ? "s" : ""} · último ping {fmtTs(d.last_at)}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Mapa + controles de playback */}
        <div className="space-y-3">
          <Card>
            <CardContent className="p-2">
              <HistoricoMap
                positions={positions}
                events={events}
                transitWindows={transitWindows}
                cursor={cursor}
                driverName={selectedDriver?.nome}
              />
            </CardContent>
          </Card>

          {selectedId && positions.length > 0 ? (
            <Card>
              <CardContent className="p-4 space-y-3" data-testid="hist-playback-controls">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        // Auto-rewind: se cursor está no fim, volta pro início antes de tocar
                        if (!playing && cursor >= positions.length - 1) setCursor(0);
                        setPlaying((p) => !p);
                      }}
                      disabled={positions.length < 2}
                      className="bg-violet-600 hover:bg-violet-700"
                      data-testid="hist-btn-play"
                    >
                      {playing ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                      {playing ? "Pausar" : "Reproduzir"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRestart}
                      disabled={positions.length < 2}
                      data-testid="hist-btn-restart"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Início
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-muted-foreground" />
                    <Select value={String(speed)} onValueChange={(v) => setSpeed(Number(v))}>
                      <SelectTrigger className="w-24 h-8" data-testid="hist-select-speed">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SPEEDS.map((s) => (
                          <SelectItem key={s} value={String(s)} data-testid={`hist-speed-${s}`}>
                            {s}× vel.
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <input
                    type="range"
                    min={0}
                    max={Math.max(0, positions.length - 1)}
                    value={cursor}
                    onChange={(e) => { setCursor(Number(e.target.value)); setPlaying(false); }}
                    className="w-full accent-violet-600"
                    data-testid="hist-slider"
                    aria-label="Cursor do trajeto"
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                    <span data-testid="hist-cursor-info">
                      Ponto <b>{cursor + 1}</b> de <b>{positions.length}</b>
                    </span>
                    <span
                      className="inline-flex items-center gap-1 font-semibold text-slate-800"
                      data-testid="hist-cursor-timestamp"
                    >
                      <MapPin className="w-3 h-3" />
                      {fmtTs(cursorPos?.captured_at || cursorPos?.server_at)}
                    </span>
                  </div>
                </div>

                {cursorPos && (
                  <div className="text-[11px] text-muted-foreground grid grid-cols-2 sm:grid-cols-4 gap-1.5 pt-1 border-t">
                    <div><b>Lat:</b> {cursorPos.latitude?.toFixed(6)}</div>
                    <div><b>Lng:</b> {cursorPos.longitude?.toFixed(6)}</div>
                    <div><b>Precisão:</b> {cursorPos.accuracy ? `${Math.round(cursorPos.accuracy)}m` : "—"}</div>
                    <div className="flex items-center gap-0.5">
                      <ChevronsRight className="w-3 h-3" />
                      <b>Vel.:</b> {Number.isFinite(cursorPos.speed) ? `${Math.round((cursorPos.speed || 0) * 3.6)} km/h` : "—"}
                    </div>
                  </div>
                )}

                {/* Legenda de cores da polyline */}
                <div className="flex items-center gap-3 text-[11px] text-slate-600 pt-1 border-t flex-wrap" data-testid="hist-legenda">
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-4 h-1 bg-blue-600 rounded-full"></span>Em Trânsito</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-block w-4 h-1 bg-slate-400 rounded-full" style={{ backgroundImage: "repeating-linear-gradient(90deg,#94a3b8 0 3px,transparent 3px 6px)" }}></span>Fora de rota</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-flex items-center justify-center w-4 h-4 bg-sky-600 rounded-full text-white text-[9px] font-bold">▶</span>Saída</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-flex items-center justify-center w-4 h-4 bg-emerald-600 rounded-full text-white text-[9px] font-bold">✓</span>Concluída</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-flex items-center justify-center w-4 h-4 bg-amber-600 rounded-full text-white text-[9px] font-bold">↻</span>Remarcada</span>
                  <span className="inline-flex items-center gap-1"><span className="inline-flex items-center justify-center w-4 h-4 bg-red-600 rounded-full text-white text-[9px] font-bold">✕</span>Cancelada</span>
                </div>
              </CardContent>
            </Card>
          ) : selectedId && loadingPositions ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">Carregando trajeto…</CardContent>
            </Card>
          ) : selectedId ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground" data-testid="hist-no-positions">
                Nenhuma posição registrada nas últimas 24h para este entregador.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground" data-testid="hist-select-driver">
                <History className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                Selecione um entregador na lista à esquerda para ver o trajeto das últimas 24h.
              </CardContent>
            </Card>
          )}

          {selectedId && positions.length > 0 && events.length > 0 && (
            <Card>
              <CardContent className="p-3" data-testid="hist-events-timeline">
                <div className="text-xs uppercase font-bold text-violet-800 mb-2 flex items-center gap-1.5">
                  <History className="w-4 h-4" />
                  Eventos do dia · clique para saltar o cursor
                </div>
                <ul className="space-y-1.5">
                  {events.map((ev, i) => {
                    const idx = findNearestPositionIdx(positions, ev.at);
                    const cfg = {
                      conclusao: { bg: "bg-emerald-50 hover:bg-emerald-100", pill: "bg-emerald-600", icon: "✓", label: "Entrega concluída" },
                      saida:     { bg: "bg-sky-50 hover:bg-sky-100",         pill: "bg-sky-600",     icon: "▶", label: "Saída da loja" },
                      remarcada: { bg: "bg-amber-50 hover:bg-amber-100",     pill: "bg-amber-600",   icon: "↻", label: "Remarcada" },
                      cancelada: { bg: "bg-rose-50 hover:bg-rose-100",       pill: "bg-red-600",     icon: "✕", label: "Cancelada" },
                    }[ev.tipo] || { bg: "bg-slate-50 hover:bg-slate-100", pill: "bg-slate-500", icon: "•", label: ev.tipo };
                    return (
                      <li
                        key={`${ev.tipo}-${ev.entrega_id || ev.at || i}`}
                        className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md cursor-pointer transition ${cfg.bg} ${idx === null ? "opacity-50 pointer-events-none" : ""}`}
                        onClick={() => { if (idx !== null) { setCursor(idx); setPlaying(false); } }}
                        data-testid={`hist-event-${i}`}
                        data-tipo={ev.tipo}
                      >
                        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-extrabold shrink-0 ${cfg.pill}`}>
                          {cfg.icon}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">
                            {cfg.label}
                            {ev.numero_pedido && <span className="text-slate-500 font-normal ml-1">#{ev.numero_pedido}</span>}
                          </div>
                          {ev.cliente_nome && (
                            <div className="text-[10px] text-slate-500 truncate">{ev.cliente_nome}</div>
                          )}
                        </div>
                        <span className="text-[10px] font-mono text-slate-700 shrink-0">{fmtTs(ev.at).split(",")[1]?.trim() || fmtTs(ev.at)}</span>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
